import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack,
  SportsEsports,
  Search as SearchIcon,
  Close as ClearIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { gamesData } from "./gamesData";
import { adminColors, TOUCH_HIT_SX, radius } from "./theme/adminTheme";

// Grade-range badge colours
const GRADE_COLORS = {
  "K-2":     { bg: "#e3f2fd", color: "#1565c0" },
  "K-5":     { bg: "#e8f5e9", color: "#2e7d32" },
  "K-8":     { bg: "#e0f2f1", color: "#00695c" },
  "K-12":    { bg: "#ede7f6", color: "#4527a0" },
  "3-5":     { bg: "#fff3e0", color: "#e65100" },
  "3-6":     { bg: "#fce4ec", color: "#ad1457" },
  "3-8":     { bg: "#fff8e1", color: "#f57f17" },
  "6-8":     { bg: "#fce4ec", color: "#c62828" },
  "6-10":    { bg: "#e8eaf6", color: "#1a237e" },
  "6-12":    { bg: "#e8eaf6", color: "#283593" },
  "9-12":    { bg: "#f3e5f5", color: "#6a1b9a" },
  "Educator":{ bg: "#f5f5f5", color: "#424242" },
};

// Provider-specific gradient backgrounds for card placeholders
const PROVIDER_GRADIENTS = {
  "Code.org":           "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
  "Minecraft Education":"linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)",
  "Google":             "linear-gradient(135deg, #ea4335 0%, #c62828 100%)",
  "Scratch Foundation": "linear-gradient(135deg, #ff7043 0%, #e64a19 100%)",
  "Microsoft":          "linear-gradient(135deg, #0078d4 0%, #005a9e 100%)",
  "Adobe":              "linear-gradient(135deg, #fa0f00 0%, #b71c1c 100%)",
  "LEGO® Education":    "linear-gradient(135deg, #e3000b 0%, #880e4f 100%)",
  "MIT":                "linear-gradient(135deg, #750014 0%, #4a0010 100%)",
  "JA Worldwide":       "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
  "imagi":              "linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)",
  "Amazon":             "linear-gradient(135deg, #ff9900 0%, #e65100 100%)",
  "IBM":                "linear-gradient(135deg, #0530ad 0%, #031a6b 100%)",
  "Meta":               "linear-gradient(135deg, #1877f2 0%, #0d5bb5 100%)",
  "Roblox":             "linear-gradient(135deg, #e2231a 0%, #9e0000 100%)",
};

const getProviderGradient = (provider) => {
  // A future activity entry missing `provider` shouldn't blank the page —
  // fall straight through to the default gradient.
  if (!provider) return `linear-gradient(135deg, ${adminColors.accent} 0%, ${adminColors.accentDark} 100%)`;
  for (const key of Object.keys(PROVIDER_GRADIENTS)) {
    if (provider.toLowerCase().includes(key.toLowerCase())) return PROVIDER_GRADIENTS[key];
  }
  return `linear-gradient(135deg, ${adminColors.accent} 0%, ${adminColors.accentDark} 100%)`;
};

// Grade ranges look like "K-2", "3-8", "9-12", or the literal "Educator".
// Sorting them numerically (Kindergarten first, Educator last) reads far
// better in a dropdown than the alphabetical order the raw strings fall
// into ("3-5" before "K-2" before "9-12" is not a useful sequence).
// Guards against a missing/malformed `gradeRange` (no match, no field) —
// such an entry just sorts to the end instead of throwing and blanking
// the whole page.
const gradeSortKey = (range) => {
  if (!range) return Number.MAX_SAFE_INTEGER;
  if (range === "Educator") return Number.MAX_SAFE_INTEGER;
  const lead = range.match(/^(K|\d+)/)?.[1];
  if (!lead) return Number.MAX_SAFE_INTEGER;
  return lead === "K" ? -1 : Number(lead);
};

const GameCard = React.memo(function GameCard({ game }) {
  const [imgError, setImgError] = useState(false);
  const gradeStyle = GRADE_COLORS[game.gradeRange] || GRADE_COLORS["K-12"];

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: radius.surface,
        border: "1px solid #e5e5e5",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "0 6px 16px rgba(0,0,0,0.14)",
          transform: "translateY(-3px)",
        },
      }}
    >
      {/* Thumbnail */}
      {game.thumbnailUrl && !imgError ? (
        <CardMedia
          component="img"
          height="162"
          image={game.thumbnailUrl}
          alt={game.title}
          onError={() => setImgError(true)}
          referrerPolicy="no-referrer"
          sx={{ objectFit: "cover" }}
        />
      ) : (
        <Box
          sx={{
            height: 162,
            background: getProviderGradient(game.provider),
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <SportsEsports sx={{ fontSize: 44, color: "rgba(255,255,255,0.9)" }} />
          <Typography
            sx={{
              color: "rgba(255,255,255,0.85)",
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              px: 1,
              textAlign: "center",
            }}
          >
            {game.provider}
          </Typography>
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1, pb: 0 }}>
        {/* Provider + Grade row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
            mb: 0.75,
          }}
        >
          <Chip
            label={game.provider || "Unknown provider"}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.62rem", height: 20, maxWidth: 140 }}
          />
          <Chip
            label={
              game.gradeRange === "Educator"
                ? "Educator"
                : game.gradeRange
                  ? `Gr. ${game.gradeRange}`
                  : "Ungraded"
            }
            size="small"
            sx={{
              fontSize: "0.62rem",
              height: 20,
              fontWeight: 700,
              bgcolor: gradeStyle.bg,
              color: gradeStyle.color,
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{
            fontSize: "0.88rem",
            lineHeight: 1.3,
            mb: 0.75,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {game.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: "0.74rem",
            lineHeight: 1.45,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {game.description}
        </Typography>
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ px: 1.5, pb: 1.5, pt: 1, gap: 1 }}>
        <Button
          variant="contained"
          size="small"
          href={game.playUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            flex: 1,
            // Dark-ink action treatment (matches the chat send button)
            // rather than the indigo accent: every provider gradient and
            // grade chip on these cards is already blue/indigo, so an
            // indigo CTA stopped reading as "the button to press."
            bgcolor: adminColors.ink,
            color: "#fff",
            "&:hover": { bgcolor: "#27272a" },
            borderRadius: radius.control,
            textTransform: "none",
            fontSize: "0.75rem",
            fontWeight: 600,
            boxShadow: "none",
          }}
        >
          Play
        </Button>

        {game.teacherUrl && (
          <Button
            variant="outlined"
            size="small"
            href={game.teacherUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              flex: 1,
              borderColor: "rgba(24,24,27,0.3)",
              color: adminColors.ink,
              "&:hover": {
                borderColor: adminColors.ink,
                bgcolor: "rgba(24,24,27,0.04)",
              },
              borderRadius: radius.control,
              textTransform: "none",
              fontSize: "0.72rem",
              fontWeight: 600,
            }}
          >
            Teacher Notes
          </Button>
        )}
      </CardActions>
    </Card>
  );
});

// Debounces a fast-changing value (keystrokes) so the 108-card grid only
// re-filters and re-renders once typing settles, instead of on every
// keystroke.
const useDebouncedValue = (value, delayMs) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

const ALL = "all";

const GamesHub = () => {
  const navigate = useNavigate();
  const totalWithTeacherNotes = gamesData.filter((g) => g.teacherUrl).length;

  const [search, setSearch] = useState("");
  const [gradeFilter, setGradeFilter] = useState(ALL);
  const [providerFilter, setProviderFilter] = useState(ALL);
  const debouncedSearch = useDebouncedValue(search, 200);

  // Filter option lists are derived from the live data set, so a new
  // provider or grade range added to gamesData shows up automatically.
  const gradeOptions = useMemo(
    () =>
      [...new Set(gamesData.map((g) => g.gradeRange))].sort(
        (a, b) => gradeSortKey(a) - gradeSortKey(b)
      ),
    []
  );
  const providerOptions = useMemo(
    () =>
      // Filter out missing `provider` before sorting — a bare .localeCompare()
      // on undefined would throw and blank the whole grid. An activity with
      // no provider still shows under "All providers", it just isn't
      // filterable by a specific one.
      [...new Set(gamesData.map((g) => g.provider).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      ),
    []
  );

  const filteredGames = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    return gamesData.filter((game) => {
      if (gradeFilter !== ALL && game.gradeRange !== gradeFilter) return false;
      if (providerFilter !== ALL && game.provider !== providerFilter) return false;
      if (query) {
        const haystack = `${game.title} ${game.description}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [debouncedSearch, gradeFilter, providerFilter]);

  const hasActiveFilters =
    search.trim() !== "" || gradeFilter !== ALL || providerFilter !== ALL;

  const clearFilters = useCallback(() => {
    setSearch("");
    setGradeFilter(ALL);
    setProviderFilter(ALL);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        backgroundColor: "#f7f7f8",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderBottom: "1px solid #e5e5e5",
          px: { xs: 2, sm: 3 },
          py: 1.75,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          position: "sticky",
          top: 0,
          zIndex: 1000,
        }}
      >
        <IconButton
          onClick={() => navigate("/chat")}
          size="small"
          sx={{ color: "#6b7280", "&:hover": { bgcolor: "#f3f4f6" }, ...TOUCH_HIT_SX }}
          aria-label="Back to Chat"
        >
          <ArrowBack />
        </IconButton>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ fontSize: "1rem", color: "#111827", lineHeight: 1.2 }}
          >
            Hour of AI — Games Hub
          </Typography>
          <Typography variant="caption" sx={{ color: "#6b7280" }}>
            {gamesData.length} activities
            &nbsp;·&nbsp; {totalWithTeacherNotes} with Teacher Notes
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: 1680,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: 3,
          boxSizing: "border-box",
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Explore curated AI &amp; CS activities. Click{" "}
          <strong>Play</strong> to launch an activity in a new tab.
          Activities that include educator resources show a{" "}
          <strong>Teacher Notes</strong> button.
        </Typography>

        {/* Filter bar */}
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: radius.surface,
            p: { xs: 1.5, sm: 2 },
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 170px 200px" },
              gap: 1.25,
            }}
          >
            <TextField
              label="Search activities"
              placeholder="Search by title or description"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
              // Force the label above the field even when empty: with a
              // placeholder also present, MUI's default unshrunk label
              // would render on top of it until the field is focused.
              InputLabelProps={{ shrink: true }}
              sx={{
                // TOUCH_HIT_SX must land on the actual input box, not the
                // TextField's root — the root is a non-interactive column
                // flex wrapper, so minHeight there just inflates empty
                // space around the field instead of enlarging its hit area.
                "& .MuiOutlinedInput-root": {
                  ...TOUCH_HIT_SX,
                  "& fieldset": { borderColor: "#e4e4e7" },
                  "&:hover fieldset": { borderColor: adminColors.accent },
                  "&.Mui-focused fieldset": { borderColor: adminColors.accent },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#a1a1aa" }} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" fullWidth>
              <InputLabel id="grade-filter-label">Grade</InputLabel>
              <Select
                labelId="grade-filter-label"
                id="grade-filter"
                label="Grade"
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e4e7" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: adminColors.accent },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: adminColors.accent },
                  // Touch target lands on the select's actual interactive
                  // element, not the FormControl root (see TextField above).
                  "& .MuiSelect-select": { ...TOUCH_HIT_SX, display: "flex", alignItems: "center" },
                }}
              >
                <MenuItem value={ALL}>All grades</MenuItem>
                {gradeOptions.map((range) => (
                  <MenuItem key={range} value={range}>
                    {range === "Educator" ? "Educator" : `Grades ${range}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="provider-filter-label">Provider</InputLabel>
              <Select
                labelId="provider-filter-label"
                id="provider-filter"
                label="Provider"
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e4e7" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: adminColors.accent },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: adminColors.accent },
                  "& .MuiSelect-select": { ...TOUCH_HIT_SX, display: "flex", alignItems: "center" },
                }}
              >
                <MenuItem value={ALL}>All providers</MenuItem>
                {providerOptions.map((provider) => (
                  <MenuItem key={provider} value={provider}>
                    {provider}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mt: 1.5,
              pt: 1.25,
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Typography
              aria-live="polite"
              variant="body2"
              sx={{ color: adminColors.inkSoft, fontWeight: 600, fontSize: "0.8rem" }}
            >
              Showing {filteredGames.length} of {gamesData.length} activities
            </Typography>

            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                size="small"
                startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                sx={{
                  ...TOUCH_HIT_SX,
                  color: adminColors.accent,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  "&:hover": { bgcolor: adminColors.accentLight },
                }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </Box>

        {/* Results */}
        {filteredGames.length === 0 ? (
          <Box sx={{ textAlign: "center", py: { xs: 6, sm: 9 }, px: 2 }}>
            {/* Accent-tinted at reduced opacity — matches SafetyFlagsView's
                empty-queue icon treatment instead of a one-off neutral grey. */}
            <SportsEsports sx={{ fontSize: 40, color: adminColors.accent, opacity: 0.45, mb: 1.5 }} />
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ color: adminColors.ink, mb: 0.5 }}
            >
              No activities match your filters
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: adminColors.inkSoft, mb: 2.5, maxWidth: 380, mx: "auto" }}
            >
              Try a different search term, or widen the grade or provider
              filter to see more results.
            </Typography>
            <Button
              variant="outlined"
              onClick={clearFilters}
              sx={{
                ...TOUCH_HIT_SX,
                borderColor: adminColors.accent,
                color: adminColors.accent,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: adminColors.accentDark,
                  bgcolor: adminColors.accentLight,
                },
              }}
            >
              Clear filters
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
                xl: "repeat(5, 1fr)",
              },
              gap: { xs: 2, sm: 2.5 },
              alignItems: "stretch",
            }}
          >
            {filteredGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GamesHub;
